import { FC, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useHistory } from 'react-router';
import { useStyles } from './styles';
import { ChangePasswordFormInput, ChangePasswordRequest, ResetPasswordFormProps } from './types';
import { getPath } from './paths';
import {
  Grid,
  FormControl,
  Button,
  InputLabel,
  Input,
  InputAdornment,
  IconButton,
  FormHelperText,
  Paper,
} from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import axios from 'server';
import { getUrlSearchValue } from 'utils/urlUtils';
import { handleCompanyUrl, handleOrganizationUrl } from 'utils/organizationUtils';
import { useParams } from 'react-router-dom';
import { OrganizationParamUrl } from 'containers/SignInEndUser/types';
export const ResetPasswordForm: FC<ResetPasswordFormProps> = observer(({ userType }) => {
  const classes = useStyles();
  const history = useHistory();
  // const url = useLocation();
  const [expiredToken, setEpiredToken] = useState(false);
  // const UrlExtracted = url.pathname.split('/')[1];
  const { domain } = useParams<OrganizationParamUrl>();
  const {
    ldapStore: { validateTokens },
    notificationStore: { openNotification },
    appPreloaderStore: { disablePreloader },
  } = useStore();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswords, setShowPasswords] = useState<boolean>(false);
  const changePasswordReq = useMutation((data: ChangePasswordRequest) => axios.post(getPath(userType), data), {
    mutationKey: 'changeAdminPasswordReq',
    onSuccess: () => {
      openNotification('success', `Password changed successfully`);
      history.push(domain ? handleOrganizationUrl(domain, '/login') : handleCompanyUrl('/login'));
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.data?.message || 'Something went wrong while changing the password.';
      openNotification('error', errorMessage);
    },
  });
  const tokenValidateReq = useMutation(
    () => {
      const token = getUrlSearchValue('token');
      const email = getUrlSearchValue('email');
      return validateTokens(token, email);
    },
    {
      mutationKey: 'tokenValidateReq',
      onSuccess: () => {
        disablePreloader();
      },
      onError: ({ message }: Error) => {
        setEpiredToken(true);
        openNotification('error', message);
      },
    }
  );

  useEffect(() => {
    return tokenValidateReq.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { control, handleSubmit, watch } = useForm<ChangePasswordFormInput>();
  const passwordValue = watch('password');

  const onSubmit = ({ password, passwordConfirm }: ChangePasswordFormInput) => {
    changePasswordReq.mutate({
      password,
      password_confirmation: passwordConfirm,
      token: getUrlSearchValue('token'),
      email: getUrlSearchValue('email'),
    });
  };

  useAppPreloader(changePasswordReq.isLoading || tokenValidateReq.isLoading);
  return tokenValidateReq.isLoading ? null : expiredToken ? (
    <div className={classes.mainError}>
      <div>
        <h1 className={classes.title}>YOU HAVE ALREADY UPDATED YOUR PASSWORD USING THIS LINK.</h1>
      </div>
      {/* <div>
        <Button
          type='submit'
          variant='contained'
          color='primary'
          onClick={() => history.push(handleOrganizationUrl(UrlExtracted, '/user/forgot-password'))}
        >
          Forget Password
        </Button>
      </div> */}
    </div>
  ) : (
    <Grid container className={classes.main} justify='center' spacing={2}>
      <Grid item xs={4}>
        <Paper className={classes.paper} elevation={3}>
          <h1 className={classes.title}>Change Password</h1>
          <Grid container justify='center'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name='password'
                render={({ field, fieldState: { invalid, error } }) => (
                  <FormControl className={classes.formControl}>
                    <InputLabel error={invalid}>Password</InputLabel>
                    <Input
                      {...field}
                      autoFocus
                      error={invalid}
                      type={showPassword ? 'text' : 'password'}
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    {invalid ? (
                      <FormHelperText error>
                        {error?.type === 'validate' ? 'Password and confirm password must be same' : 'Required field'}
                      </FormHelperText>
                    ) : null}
                  </FormControl>
                )}
                defaultValue=''
                control={control}
                rules={{ required: true }}
              />
              <Controller
                name='passwordConfirm'
                render={({ field, fieldState: { invalid, error } }) => (
                  <FormControl className={classes.formControl}>
                    <InputLabel error={invalid}>Confirm Password</InputLabel>
                    <Input
                      {...field}
                      error={invalid}
                      type={showPasswords ? 'text' : 'password'}
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={() => setShowPasswords(!showPasswords)}
                          >
                            {showPasswords ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    {invalid ? (
                      <FormHelperText error>
                        {error?.type === 'validate' ? 'Password and confirm password must be same' : 'Required field'}
                      </FormHelperText>
                    ) : null}
                  </FormControl>
                )}
                defaultValue=''
                control={control}
                rules={{ required: true, validate: (value) => value === passwordValue }}
              />
              <Button className={classes.submitBtn} type='submit' variant='contained' color='primary'>
                Submit
              </Button>
            </form>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
});
